/**
 * InitiateCheckout Webhook for India Market
 * 
 * Sends InitiateCheckout event to n8n when:
 * 1. Buy Now button is clicked on product page
 * 2. Checkout button is clicked on cart drawer/cart page
 * 3. Razorpay Magic Checkout is initialized
 */

(function() {
  'use strict';
  
  // Early initialization - don't wait for helper
  // We'll check helper availability when sending webhook
  let initiateCheckoutFired = false;
  let lastCartHash = null;
  let webhookSentForCurrentCheckout = false;
  
  // Generate hash from cart items for deduplication
  function getCartHash(cart) {
    if (!cart || !cart.items) return null;
    const items = cart.items.map(item => 
      `${item.product_id}-${item.variant_id}-${item.quantity}`
    ).sort().join('|');
    return items;
  }
  
  // Fetch cart data
  function getCartData() {
    return fetch('/cart.js')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch cart');
        return res.json();
      })
      .catch(() => null);
  }
  
  // Send InitiateCheckout webhook with retry logic
  async function sendInitiateCheckoutWebhook(retryCount = 0) {
    const maxRetries = 10;
    const retryDelay = 200; // 200ms between retries
    
    // Check if helper is available
    if (!window.WebhookHelperIndia) {
      if (retryCount < maxRetries) {
        console.log(`[InitiateCheckout India] WebhookHelperIndia not available yet, retrying... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return sendInitiateCheckoutWebhook(retryCount + 1);
      } else {
        console.error('[InitiateCheckout India] ❌ WebhookHelperIndia not available after max retries, cannot send webhook');
        return;
      }
    }
    
    // Prevent duplicate events for same cart
    const cart = await getCartData();
    if (!cart || !cart.items || cart.items.length === 0) {
      console.warn('[InitiateCheckout India] Cart is empty, skipping webhook');
      return;
    }
    
    const cartHash = getCartHash(cart);
    if (cartHash === lastCartHash && initiateCheckoutFired) {
      console.log('[InitiateCheckout India] Same cart, already fired, skipping');
      return; // Same cart, already fired
    }
    
    lastCartHash = cartHash;
    initiateCheckoutFired = true;
    
    console.log('[InitiateCheckout India] Sending InitiateCheckout webhook...', { itemCount: cart.items.length });
    
    // Build contents array with variant IDs
    const contents = cart.items.map(item => {
      const variantId = item.variant_id.toString();
      const prefixedIds = window.WebhookHelperIndia.prefixContentIds([variantId]);
      const priceInCents = item.final_price || item.price || item.line_price || 0;
      const priceInINR = window.WebhookHelperIndia.convertPriceToINR(priceInCents);
      
      return {
        id: prefixedIds[0],
        sku: prefixedIds[0],
        item_price: priceInINR,
        quantity: item.quantity || 1,
        currency: 'INR'
      };
    });
    
    // Get all variant IDs for content_ids
    const contentIds = cart.items.map(item => {
      const variantId = item.variant_id.toString();
      const prefixedIds = window.WebhookHelperIndia.prefixContentIds([variantId]);
      return prefixedIds[0];
    });
    
    // Calculate total value
    const totalPriceInCents = cart.total_price || 0;
    const totalPriceInINR = window.WebhookHelperIndia.convertPriceToINR(totalPriceInCents);
    const totalQuantity = cart.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    
    // Build cart data
    const cartData = {
      total_price: totalPriceInCents,
      total_price_converted: totalPriceInINR,
      item_count: cart.items.length,
      items: cart.items.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity || 1,
        price_in_cents: item.final_price || item.price || item.line_price || 0
      }))
    };
    
    window.WebhookHelperIndia.sendWebhook('InitiateCheckout', {
      custom_data: {
        value: totalPriceInINR, // Total in INR
        currency: 'INR',
        content_type: 'product_group',
        content_ids: contentIds, // All variant IDs
        num_items: totalQuantity,
        gateway: 'razorpay',
        checkout_type: 'razorpay_magic_checkout',
        contents: contents
      },
      cart_data: cartData
    });
    
    // Reset flag after 2 seconds to allow new checkout attempts
    setTimeout(() => {
      initiateCheckoutFired = false;
    }, 2000);
  }
  
  // Handle Buy Now button click (product page)
  function handleBuyNowClick() {
    // Wait a bit for cart to update after adding product
    setTimeout(() => {
      sendInitiateCheckoutWebhook();
    }, 300);
  }
  
  // Handle checkout button click (cart drawer/cart page)
  function handleCheckoutClick(event) {
    // Mark that we're initiating checkout
    webhookSentForCurrentCheckout = false;
    
    // Send webhook (non-blocking, fire and forget)
    sendInitiateCheckoutWebhook().then(() => {
      webhookSentForCurrentCheckout = true;
    });
    
    // Don't prevent default - let Razorpay handle the checkout
    // The webhook is sent asynchronously, so it won't block checkout
  }
  
  // Hook into Razorpay's openRzpCheckout function
  function hookIntoRazorpayCheckout() {
    // Wait for Razorpay to load
    if (typeof window.openRzpCheckout === 'function') {
      const originalOpenRzpCheckout = window.openRzpCheckout;
      
      window.openRzpCheckout = function() {
        console.log('[InitiateCheckout India] Razorpay checkout opening, sending webhook...');
        
        // Send webhook if not already sent for this checkout
        if (!webhookSentForCurrentCheckout) {
          sendInitiateCheckoutWebhook().then(() => {
            webhookSentForCurrentCheckout = true;
          });
        }
        
        // Call original function
        return originalOpenRzpCheckout.apply(this, arguments);
      };
      
      console.log('[InitiateCheckout India] ✅ Hooked into Razorpay openRzpCheckout');
    } else {
      // Retry after a short delay
      setTimeout(hookIntoRazorpayCheckout, 200);
    }
  }
  
  // Setup all listeners
  function setupListeners() {
    // Listen for Buy Now button clicks (from buy-now-india.js)
    document.addEventListener('buy-now-india:clicked', function() {
      console.log('[InitiateCheckout India] Buy Now button clicked, sending webhook...');
      webhookSentForCurrentCheckout = false;
      handleBuyNowClick();
    });
    
    // CRITICAL: Intercept checkout button clicks BEFORE Razorpay
    // Use capture phase and check early to beat Razorpay's interception
    document.addEventListener('click', function(event) {
      const target = event.target;
      const checkoutButton = target.closest('button[name="checkout"]') || 
                            target.closest('.cart__checkout') ||
                            target.closest('[data-cart-checkout-button] button[name="checkout"]');
      
      if (checkoutButton) {
        // Check if it's in cart drawer or cart page
        const isInCartDrawer = checkoutButton.closest('.cart-drawer') !== null ||
                               checkoutButton.closest('[data-cart-drawer]') !== null ||
                               checkoutButton.closest('#cart-drawer') !== null;
        const isInCartPage = checkoutButton.closest('.cart') !== null || 
                            checkoutButton.closest('[data-cart-page]') !== null ||
                            window.location.pathname.includes('/cart');
        
        if (isInCartDrawer || isInCartPage) {
          console.log('[InitiateCheckout India] ✅ Checkout button clicked (captured early), sending webhook...');
          webhookSentForCurrentCheckout = false;
          handleCheckoutClick(event);
        }
      }
    }, true); // Capture phase - intercepts BEFORE bubbling phase
    
    // CRITICAL: Intercept form submissions BEFORE Razorpay
    document.addEventListener('submit', function(event) {
      const form = event.target;
      
      // Check if this is a cart form
      const isCartForm = form && (
        (form.id === 'cartForm') ||
        (form.classList && form.classList.contains('cart__form')) ||
        (form.action && form.action.includes('/cart')) ||
        form.closest('.cart-drawer') !== null ||
        form.closest('[data-cart-drawer]') !== null ||
        form.closest('#cart-drawer') !== null ||
        form.closest('.cart') !== null ||
        form.closest('[data-cart-page]') !== null
      );
      
      if (isCartForm) {
        const checkoutButton = form.querySelector('button[name="checkout"]') ||
                              form.querySelector('input[name="checkout"]') ||
                              form.querySelector('button.cart__checkout');
        
        if (checkoutButton) {
          console.log('[InitiateCheckout India] ✅ Checkout form submitted (captured early), sending webhook...');
          webhookSentForCurrentCheckout = false;
          handleCheckoutClick(event);
        }
      }
    }, true); // Capture phase - intercepts BEFORE bubbling phase
    
    // Hook into Razorpay's checkout function
    hookIntoRazorpayCheckout();
    
    // Also try hooking after a delay in case Razorpay loads later
    setTimeout(hookIntoRazorpayCheckout, 1000);
    setTimeout(hookIntoRazorpayCheckout, 3000);
  }
  
  // Start setting up listeners immediately (don't wait for DOMContentLoaded)
  // This ensures we intercept BEFORE Razorpay's script runs
  if (document.readyState === 'loading') {
    // Set up listeners immediately, even before DOM is ready
    // This ensures we're ready before Razorpay intercepts
    setupListeners();
    
    // Also set up again after DOM is ready (in case we missed anything)
    document.addEventListener('DOMContentLoaded', setupListeners);
  } else {
    setupListeners();
  }
  
  // Also set up after a short delay to catch late-loading elements
  setTimeout(setupListeners, 100);
  
})();
